import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface InventoryItem {
  id: string;
  name: string;
  sku: string | null;
  category: string;
  unit: string | null;
  partNumber: string | null;
  location: string | null;
  compatibleMakes: string[] | null;
  compatibleModels: string[] | null;
  quantity: number;
  minQuantity: number;
  /** Nulo sem estoque.custo. */
  unitCost: number | null;
  avgCost: number | null;
  salePrice: number;
  lastPurchasePrice: number | null;
  lastSupplierId: string | null;
  supplier: string | null;
  lowStock: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryAlert {
  id: string;
  name: string;
  quantity: number;
  minQuantity: number;
  severity: 'out_of_stock' | 'low_stock';
}

interface InventoryFilters {
  category?: string;
  name?: string;
  lowStockOnly?: boolean;
}

export function useInventory(filters: InventoryFilters = {}) {
  const queryClient = useQueryClient();

  const params = new URLSearchParams();
  if (filters.category) params.set('category', filters.category);
  if (filters.name) params.set('name', filters.name);
  if (filters.lowStockOnly) params.set('lowStockOnly', 'true');
  const qs = params.toString();

  const itemsQuery = useQuery<{ items: InventoryItem[] }>({
    queryKey: ['inventory', filters],
    queryFn: async () => {
      const res = await api.get(`/inventory${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });

  const alertsQuery = useQuery<{ count: number; items: InventoryAlert[] }>({
    queryKey: ['inventory-alerts'],
    queryFn: async () => {
      const res = await api.get('/inventory/alerts');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      sku?: string | null;
      category: string;
      compatibleMakes?: string[];
      compatibleModels?: string[];
      quantity?: number;
      minQuantity?: number;
      unitCost?: number;
      salePrice: number;
      supplier?: string | null;
    }) => {
      const res = await api.post('/inventory', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      name?: string;
      sku?: string | null;
      category?: string;
      compatibleMakes?: string[];
      compatibleModels?: string[];
      minQuantity?: number;
      unitCost?: number;
      salePrice?: number;
      supplier?: string | null;
    }) => {
      const res = await api.patch(`/inventory/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    },
  });

  const applySuggestionMutation = useMutation({
    mutationFn: async ({ id, newMinQuantity }: { id: string; newMinQuantity: number }) => {
      const res = await api.post(`/inventory/${id}/apply-suggestion`, { newMinQuantity });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-insights'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    },
  });

  const movementMutation = useMutation({
    mutationFn: async ({ itemId, ...data }: {
      itemId: string;
      type: 'in' | 'out' | 'adjustment';
      quantity: number;
      osId?: string | null;
      notes?: string | null;
    }) => {
      const res = await api.post(`/inventory/${itemId}/movement`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    },
  });

  return {
    items: itemsQuery.data?.items ?? [],
    isLoading: itemsQuery.isLoading,
    error: itemsQuery.error,
    isError: itemsQuery.isError,
    refetch: itemsQuery.refetch,
    alerts: alertsQuery.data?.items ?? [],
    alertCount: alertsQuery.data?.count ?? 0,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    recordMovement: movementMutation.mutateAsync,
    applySuggestion: applySuggestionMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isMoving: movementMutation.isPending,
    isApplyingSuggestion: applySuggestionMutation.isPending,
  };
}

/** Lightweight hook for just alerts (used by Dashboard) */
export function useInventoryAlerts() {
  const alertsQuery = useQuery<{ count: number; items: InventoryAlert[] }>({
    queryKey: ['inventory-alerts'],
    queryFn: async () => {
      const res = await api.get('/inventory/alerts');
      return res.data;
    },
  });

  return {
    alerts: alertsQuery.data?.items ?? [],
    alertCount: alertsQuery.data?.count ?? 0,
    isLoading: alertsQuery.isLoading,
  };
}
